/**
 * Decide whether a chat stream (SSE) failure should be reported to Sentry.
 *
 * The chat stream surfaces very different kinds of failure through the same
 * `error` event, and only one class is ours to fix:
 *
 *   - 5xx — the server failed to produce a response. Real, actionable
 *     problems we want in Sentry.
 *   - 4xx — expected, fully-handled business conditions: 402 free-quota
 *     exhausted (dedicated plan-upgrade UI), 401 session expired, 429
 *     rate-limit, 413/422 problematic user files, 400 message-too-long.
 *     handleError shows a purpose-built message for each; nothing is broken
 *     on our side.
 *   - No HTTP status at all (status 0 / undefined / NaN) — the transport
 *     dropped: connection lost, user navigated away, a mobile network blip
 *     aborted a large upload. Benign client-side events.
 *
 * Reporting anything but the first class floods Sentry. Transport drops
 * inflated `chat_sse_error` (CHAT-MARITACA-25) to 138 users; once those were
 * gated, expected 4xx became the flood: 850 of the next 1021 events (83%,
 * 706 users over 14 days) were 402 quota-exhausted "errors", burying the
 * genuine 5xx failures sharing the same fingerprint. Only server faults
 * (status >= 500) are worth reporting.
 */
/**
 * Whether an SSE `error` event came from the backend (an `event: error` frame,
 * whose payload sse.js delivers as the raw string) rather than from sse.js
 * itself for a transport failure (an object with status/responseText).
 *
 * A backend error is the turn's final word — the generation failed and the
 * payload says why. Treating it as a dropped connection (it has no HTTP
 * status either) handed the turn to the bg-stream resume, which found the run
 * "done" and refreshed history: the chat ended blank, with no message.
 */
export function isServerSentError(e: unknown): boolean {
  return typeof (e as any)?.data === 'string';
}

/** The message of a backend `error` frame, or the generic fallback. */
export function serverErrorMessage(data: unknown): string {
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    if (parsed && typeof parsed.message === 'string' && parsed.message.trim()) return parsed.message.trim();
  } catch {}
  return 'Desculpe, aconteceu algo errado nos nossos servidores. Por favor tente mais tarde.';
}

export function shouldReportSseError(status: unknown): boolean {
  return typeof status === 'number' && Number.isFinite(status) && status >= 500;
}
